// Buffer global first — see the module's comment; import order matters here.
import './polyfills';
import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import App from './modules/App/_app';
import ErrorPage from './modules/App/pages/ErrorPage';

import './styles/main.css';
import { logRumError } from './modules/App/_rum';
import { installChromeTranslateGuard } from './modules/App/chromeTranslateGuard';
import { applyLocale, setLocaleChangeHandler } from './i18n';
import { useLocaleStore } from './stores/useLocaleStore';
import { readLocaleFromQuery } from './i18n/locales';
import { installZodErrorMap } from './helpers/validationMessages';
import { registerLocale, trackLocaleChanged } from './helpers/mixpanel';
import { setRumLocale } from './modules/App/_rum';
import queryClient from './queryClient';
import { shouldReloadOnPreloadError } from './modules/Common/utils/chunkLoadState';
import { isEmbedPath } from './modules/Embed/useEmbedConfig';

// Install BEFORE React renders so the Node.prototype patches are in place
// by the time React touches the DOM. The earlier React imports don't touch
// the DOM themselves — only createRoot(...).render(...) below does — so
// running the install here still beats every real DOM op. See
// chromeTranslateGuard.js for the rationale: this is the canonical
// workaround for the "Failed to execute 'removeChild' on 'Node'" crash
// when users have Chrome Translate (or similar text-replacing extensions)
// enabled.
installChromeTranslateGuard();

// Resolve the UI language (persisted choice > ?lng= override > browser > 'en')
// and load its eager namespaces BEFORE first paint, so a Spanish user's reload
// never flashes English. The eager bundles are three small JSON files, so the
// added time-to-first-paint is single-digit milliseconds.
//
// Kept out of module scope in _app.jsx deliberately: this must run before any
// component reads i18n, and the entry module is the only place that is
// guaranteed.
const queryLocale = readLocaleFromQuery();
if (queryLocale) {
  useLocaleStore.getState().setLocale(queryLocale);
} else {
  useLocaleStore.getState().initFromBrowser();
}

// One place reacts to every locale change:
//   - Clear the React Query cache. Once the backend localizes responses the
//     locale is part of cache identity; clearing is cheaper and safer than
//     threading the locale through every query key, where one miss serves one
//     language's data under another. Switches are rare, so the refetch is
//     imperceptible.
//   - Re-register the Mixpanel locale super-property and emit an explicit
//     switch event, and tag the RUM session — so analytics and crash reports
//     are segmentable by language (phase 9 §3).
setLocaleChangeHandler((code, previous) => {
  queryClient.clear();
  registerLocale(code);
  setRumLocale(code);
  if (previous && previous !== code) trackLocaleChanged(previous, code);
});

// Seed the super-property / RUM attribute for the boot locale too, so events
// before the first manual switch still carry it.
registerLocale(useLocaleStore.getState().locale);
setRumLocale(useLocaleStore.getState().locale);

// Translate every zod constraint that carries no explicit message. Installed
// once, globally, so ~500 existing schema definitions need no edit at all —
// see helpers/validationMessages.js for why a schema cannot call t() itself.
installZodErrorMap();

// Global error handler for uncaught exceptions
const handleGlobalErrors = (event) => {
  // `event.error` is null for cross-origin scripts and some extension
  // content-script failures. The old fallback `new Error('Unknown error
  // occurred')` threw away `event.message` and the source location — the
  // only identifying information those events carry — which made QA
  // failures land in the console as literally "Unknown error occurred".
  const error =
    event.error ||
    new Error(
      `${event.message || 'Unknown error'} (${event.filename || 'unknown source'}:${
        event.lineno ?? '?'
      }:${event.colno ?? '?'})`,
    );
  logRumError(error, {
    source: 'window.onerror',
    isFatalError: true,
  });
  // Prevent default browser error handling. NB this SUPPRESSES the browser's
  // own console line for the uncaught error — logRumError's console.error is
  // the replacement, so it must stay informative.
  event.preventDefault();
};

// Register global error handlers
window.addEventListener('error', handleGlobalErrors);

// Vite fires vite:preloadError when a modulepreload for a lazy chunk fails. The
// usual cause is a deploy that replaced the hashed assets while this tab was
// open, so the URL in the loaded manifest now 404s. A reload re-resolves the
// manifest, and it is the ONLY real recovery: the HTML module map records a
// failed module fetch, so re-importing the same specifier rejects without even
// hitting the network (Chromium follows the spec here), which is why
// lazyWithRetry's in-importer retry cannot rescue a stale deploy on its own.
//
// Vite's error is `TypeError: Failed to fetch dynamically imported module` — it
// is NOT webpack's ChunkLoadError, so never match on that name here.
//
// Deliberately NOT calling event.preventDefault(). Vite wraps every chunked
// dynamic import as __vitePreload(...), whose handler rethrows only while the
// event's default is not prevented. Preventing it makes the failed import
// RESOLVE with undefined instead of rejecting, so React.lazy would read
// `.default` off undefined and the boundary would report that TypeError instead
// of the real fetch failure — and lazyWithRetry's .catch would never run at all.
// Letting Vite rethrow keeps the accurate error flowing to the ErrorBoundary,
// which is the single fatal reporter and the only one with a componentStack.
const STALE_CHUNK_RELOAD_KEY = 'cg:stale-chunk-reload-path';
const RUNNING_BUILD = new URL(import.meta.url).pathname;
// The reload itself is asynchronous, so the rejected import keeps propagating
// until the document unloads. This flag stops a second event in that window
// from queueing another reload.
let reloadScheduled = false;
const failedChunkImports = new Set();

window.addEventListener('vite:preloadError', (event) => {
  failedChunkImports.add(event.payload);
  if (reloadScheduled) return;
  // Skip only when the sole imports in flight are background preloads: a failed
  // guess is harmless, and the navigation that later needs the chunk fails
  // through lazyWithRetry and lands back here with a route import pending.
  // While a route import is pending, a failing background preload reloads too —
  // accepted; the user is waiting on a chunk anyway and the per-route guard
  // below caps it at one.
  if (!shouldReloadOnPreloadError()) return;

  // One reload per route per build per session. Keyed on the path rather than a
  // timestamp: on a slow connection - exactly when fetches fail - a boot taking
  // longer than a time window would re-arm the guard and loop. Payload messages
  // only carry the failing URL on some browsers, so the path is the portable
  // key. sessionStorage throws in some privacy modes, so neither read nor write
  // may break the handler.
  const guardKey = `${STALE_CHUNK_RELOAD_KEY}:${RUNNING_BUILD}:${window.location.pathname}`;
  let alreadyReloaded = false;
  try {
    alreadyReloaded = window.sessionStorage.getItem(guardKey) === '1';
  } catch {
    alreadyReloaded = false;
  }

  if (alreadyReloaded) {
    // Reloading did not help, so the asset is genuinely gone rather than stale.
    // Let the error propagate: the ErrorBoundary reports it as fatal, with a
    // componentStack, and shows the user something rather than looping.
    return;
  }

  try {
    window.sessionStorage.setItem(guardKey, '1');
  } catch {
    // Without a guard a reload could loop, so prefer showing the error.
    return;
  }

  reloadScheduled = true;
  window.location.reload();
});

const rootElement = document.getElementById('root');
const root = createRoot(rootElement);

const render = () =>
  root.render(
    <StrictMode>
      <ErrorBoundary
        FallbackComponent={ErrorPage}
        onError={(error, info) =>
          logRumError(
            error,
            reloadScheduled && failedChunkImports.has(error)
              ? { source: 'stale-deploy-reload', isFatalError: false }
              : { ...info, source: 'ErrorBoundary', isFatalError: true },
          )
        }
        onReset={() => {
          // Reset application state when user attempts recovery
          if (isEmbedPath(window.location.pathname)) {
            window.location.reload();
          } else {
            window.location.href = '/';
          }
        }}
      >
        <App />
      </ErrorBoundary>
    </StrictMode>,
  );

// Load the resolved locale's resources, THEN render. Deliberately a promise
// chain rather than top-level await, which would need the browserslist
// production targets to support it.
//
// The catch is load-bearing: a failed locale load must degrade to an English
// app, never to a white screen. Every t() call carries its English default, so
// rendering with zero resources loaded is a fully functional English UI.
applyLocale(useLocaleStore.getState().locale)
  .catch((error) => {
    logRumError(error, { source: 'i18n-boot', isFatalError: false });
  })
  .finally(render);
